import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import { Button } from '@/components/UI/Button/Button';
import { ChatIcon } from '@/components/UI/Icons/Icons';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { Popup } from '@/components/UI/Popup/Popup';
import { getLoginUrlWithReturn, sleep } from '@/utils/helpers';
import { popupData } from '@/components/Ad/FloatingButtons/ChatButton.constants';
import { useCustomPush } from '@/components/CustomLink/CustomLink.hooks';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { useAuthStore } from '@/store/useAuthStore';
import { useAdDetails } from '@/views/Ads/useAdDetails';
import { useFloatingButtonsVisibility } from '@/hooks/useFloatingButtonsVisibility';
import { UNBLOCK_USER } from '@/api/mutations/users.mutations';
import { useServerDataContext } from '@/contexts';

export const ChatButton = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const customPush = useCustomPush();
  const [isPopupOpen, popupToggle] = useToggleOpenState();
  const [popupType, setPopupType] = useState<
    null | 'blockedByYou' | 'blockedByOtherUser'
  >(null);
  const { adDetails } = useAdDetails();
  const { initialAdDetails } = useServerDataContext();
  const adData = adDetails ?? initialAdDetails;
  const { isChatAvailable } = useFloatingButtonsVisibility();
  const [isAuthenticated] = useAuthStore(state => [state.isAuthenticated]);

  const [unblockUser, { loading: isUnblocking }] = useMutation(UNBLOCK_USER);

  if (!adData || !isChatAvailable) return;

  const {
    chatPath,
    user: { id, isBlockedByMe, isBlockedMe },
  } = adData;

  async function closePopup() {
    popupToggle();
    await sleep(500);
    setPopupType(null);
  }

  async function unblock() {
    await unblockUser({ variables: { userId: id } });
    customPush(chatPath);
  }

  function startChat() {
    if (!adData) return;

    if (!isAuthenticated) {
      customPush(getLoginUrlWithReturn(chatPath, router.asPath));
      return;
    }

    if (isBlockedByMe) {
      popupToggle();
      setPopupType('blockedByYou');
      return;
    }

    if (isBlockedMe) {
      popupToggle();
      setPopupType('blockedByOtherUser');
      return;
    }

    customPush(chatPath);
  }

  return (
    <>
      <Button
        appearance="secondary"
        isFullWidth
        onClick={startChat}
        data-testid={AdPageTestIds.chatButton}
        data-stat={`${adData?.shop ? 'shop-' : ''}ad-message-form`}
      >
        <ChatIcon /> {t('common.write')}
      </Button>
      <Popup
        isOpen={isPopupOpen}
        close={closePopup}
        {...(popupType &&
          popupData(t, popupToggle, isUnblocking, unblock, closePopup)[
            popupType
          ])}
      />
    </>
  );
};
